import React from 'react';
import {
  DocPage, PageHeader, Section,
  Para, Callout, DefList, InlineCode, Prerequisites, NextStep, Tooltip,
} from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import './Fee.css';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'invoice',  label: 'Invoice Line Items' },
];

const ATTRIBUTES = [
  {
    term: 'name',
    type: 'string',
    desc: 'Display name shown on the customer\'s invoice (e.g. "Courier Charges").',
  },
  {
    term: 'description',
    type: 'string',
    desc: 'Internal note for your team. Not visible to the end customer.',
  },
  {
    term: 'hsn_code',
    type: 'string',
    desc: 'Harmonized System Nomenclature code. Required for GST reporting in India, EU VAT in Europe, and similar tax regimes.',
  },
];

// ── Invoice mock ──────────────────────────────────────────────────────────────

function InvoiceMock({ invoice }) {
  const { id, due, rows, total } = invoice;
  return (
    <div className="fc-invoice">
      <div className="fc-invoice-top">
        <span className="fc-invoice-id">Invoice #{id}</span>
        <span className="fc-invoice-due">{due}</span>
      </div>
      <table className="fc-invoice-table">
        <thead>
          <tr>
            <th>Item</th><th>Qty</th><th>Rate</th><th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.name} className={`fc-invoice-row fc-invoice-row--${row.accent}`}>
              <td>
                <span className="fc-invoice-item-name">{row.name}</span>
                <span className="fc-invoice-item-note">{row.note}</span>
              </td>
              <td className="fc-invoice-num">{row.qty}</td>
              <td className="fc-invoice-num">{row.rate}</td>
              <td className="fc-invoice-num">{row.amount}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="fc-invoice-footer">
        <div className="fc-legend">
          {rows.map((row) => (
            <span key={row.name} className="fc-legend-item">
              <span className={`fc-legend-dot fc-legend-dot--${row.accent}`} />
              {row.name}
            </span>
          ))}
        </div>
        <div className="fc-invoice-total">Total <strong>{total}</strong></div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Fee({ onNavigate }) {
  const { industry } = useIndustry();
  const { examples, invoice } = industry.feeComponent;
  const [ex0, ex1, ex2] = examples;
  const description = (
    <>
      A Fee is one billable service within a product — each one appears as a distinct line on the
      invoice, priced through its own{' '}
      <Tooltip tip="The pricing rule for a fee — which model to use (fixed, per-unit, tiered) and what the rates are. Each fee and pricing dimension combination gets its own rate card.">
        rate card
      </Tooltip>
      .
    </>
  );

  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Products"
        title="Fee"
        description={description}
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[{ id: 'product', label: 'Products' }]}
      />

      <Section id="overview" title="Overview">
        <Para>
          A fee is the monetization unit of a product. It defines one thing you charge for,
          links to the meter that measures it, and connects to the rate card that
          prices it. For a {industry.label.toLowerCase()} company that might be{' '}
          <InlineCode>{ex0}</InlineCode> and <InlineCode>{ex1}</InlineCode>
          {ex2 ? <>, or <InlineCode>{ex2}</InlineCode></> : null}.
          Fees are defined once at the product level and apply across all customers.
        </Para>
        <Para>Every fee carries a set of identifying attributes:</Para>
        <DefList items={ATTRIBUTES} />
      </Section>

      <Section id="invoice" title="Invoice Line Items">
        <Para>
          Each fee you define appears as a distinct line item on the customer's invoice.
          A customer billed across three fees will see three separate lines — each with
          its own quantity, rate, and amount.
        </Para>
        <InvoiceMock invoice={invoice} />
        <Callout type="tip" title="One fee, one line item">
          By default, each fee maps to exactly one invoice line.{' '}
          <Tooltip tip="Attributes defined on a product that allow a fee to carry different prices for different values. Each unique combination of attribute values gets its own invoice line.">
            Pricing Dimensions
          </Tooltip>
          {' '}can expand a single fee into multiple lines — one per pricing variation.
        </Callout>
      </Section>

      <NextStep
        onNavigate={onNavigate}
        items={[
          { id: 'meter', title: 'Meters', desc: 'How the billing engine measures consumption to drive a fee.' },
          { id: 'pricing-dimensions', title: 'Pricing Dimensions',  desc: 'How the same fee carries different prices across zones, brands, or tiers.' },
          { id: 'rate-card',       title: 'Rate Cards',       desc: 'The pricing rule for each fee and attribute combination.' },
        ]}
      />
    </DocPage>
  );
}
